/**
 * Entdeckt-Inbox (Stufe A „Entdecken") — die App findet, was du noch nicht
 * kennst: Der Nachtlauf scannt ~50 Welt-ETFs außerhalb der Watchlist; Treffer
 * („dreht auf" oder Score ≥ 60) landen hier. 1-Klick-Übernahme in die
 * Watchlist (AddSymbolDialog-Muster wie beim Experten-Radar) oder Ablehnen —
 * Abgelehnte kommen nicht wieder.
 */

import { Compass } from 'lucide-react';

import { useDataState } from '@/hooks/use-data-state';
import {
  decideDiscoverySuggestion,
  fetchDiscoverySuggestions,
  type DiscoverySuggestion,
} from '@/lib/discovery-read';
import { GRUPPE_LABEL, discoveryTargetLayer } from '@/lib/discovery-universe';
import { cn } from '@/lib/utils';

const FLOW_CHIP: Record<string, { text: string; cls: string }> = {
  dreht_auf: { text: 'dreht auf', cls: 'border-amber-600/60 bg-amber-950/40 text-amber-200' },
  zufluss: { text: 'Geld fließt rein', cls: 'border-emerald-700/60 bg-emerald-950/30 text-emerald-200' },
  seitwaerts: { text: 'unauffällig', cls: 'border-zinc-700/60 bg-zinc-900/40 text-zinc-400' },
  dreht_ab: { text: 'bröckelt', cls: 'border-orange-700/60 bg-orange-950/30 text-orange-200' },
  abfluss: { text: 'Geld fließt raus', cls: 'border-red-800/60 bg-red-950/25 text-red-300' },
  unbekannt: { text: 'Daten dünn', cls: 'border-zinc-800/60 bg-zinc-900/20 text-zinc-600' },
};

interface Props {
  /** Öffnet den AddSymbolDialog mit vorausgefülltem Symbol + passendem Layer. */
  onAdd: (symbol: string, prefill: { layer: string; assetClass: string }) => void;
}

export function EntdecktCard({ onAdd }: Props) {
  const suggestions = useDataState({
    queryKey: ['discovery-suggestions'],
    queryFn: () => fetchDiscoverySuggestions(),
    ttlSeconds: 15 * 60,
    isEmpty: (rows) => !rows || rows.length === 0,
  });

  const rows = suggestions.state.data ?? [];

  async function decide(s: DiscoverySuggestion, status: 'uebernommen' | 'abgelehnt') {
    try {
      await decideDiscoverySuggestion(s.symbol, status);
    } catch {
      // fail-quiet — Refetch zeigt die Wahrheit
    }
    suggestions.refetch();
  }

  return (
    <details className="rounded-md border border-zinc-800/80 bg-zinc-900/30 group/entdeckt">
      <summary className="cursor-pointer p-3 min-h-[44px] list-none flex items-center gap-2 hover:bg-zinc-800/40 rounded-md">
        <span className="text-cockpit-accent inline-block transition-transform group-open/entdeckt:rotate-90">
          ▸
        </span>
        <Compass className="size-4 text-amber-300 shrink-0" />
        <span className="text-sm font-semibold text-zinc-200">
          Entdeckt — passt zur Lage
        </span>
        <span className="text-[10px] text-zinc-500">
          {rows.length > 0
            ? `${rows.length} Vorschläge aus dem Welt-Universum`
            : 'Scan aktiv — aktuell dreht nichts von unten auf (in Gier-/Neutral-Phasen normal)'}
        </span>
      </summary>
      <div className="px-3 pb-3">
        <p className="mb-2 text-[11px] leading-relaxed text-zinc-500">
          Der Nachtlauf scannt ~50 Welt-ETFs <em>außerhalb</em> deiner Watchlist (Themen,
          Regionen, Rohstoffe) mit derselben Bewertung wie deine Werte.{' '}
          <span className="text-amber-300/90">„dreht auf"</span> = Geld beginnt gerade zu
          fließen — früh dran statt dem Zug hinterher. Übernehmen legt den Wert in deine
          Watchlist; Abgelehnte kommen nicht wieder.
        </p>
        {rows.length === 0 ? (
          <p className="text-xs text-zinc-500">
            Noch keine offenen Vorschläge — entweder ist gerade nichts interessant genug
            (ehrlich!), oder der erste Scan steht noch aus.
          </p>
        ) : (
          <div className="divide-y divide-zinc-800/60">
            {rows.map((s) => {
              const chip = FLOW_CHIP[s.flow_state ?? 'unbekannt'] ?? FLOW_CHIP.unbekannt!;
              return (
                <div key={s.symbol} className="flex flex-wrap items-center gap-2 py-2">
                  <div className="min-w-0 flex-1 basis-32">
                    <div className="truncate text-sm font-medium text-zinc-100">
                      {s.label}{' '}
                      <span className="font-mono text-[10px] text-zinc-500">{s.symbol}</span>
                    </div>
                    <div className="text-[10px] text-zinc-500">
                      {GRUPPE_LABEL[s.gruppe] ?? s.gruppe}
                    </div>
                  </div>
                  <span
                    className={cn(
                      'inline-flex shrink-0 items-center rounded-full border px-2 py-0.5 text-[10px] font-medium',
                      chip.cls
                    )}
                  >
                    {chip.text}
                  </span>
                  {s.score !== null && (
                    <span className="shrink-0 font-mono text-xs text-zinc-300">
                      Score {s.score}
                    </span>
                  )}
                  {s.rs13 !== null && (
                    <span className="shrink-0 font-mono text-[10px] text-zinc-500">
                      {s.rs13 > 0 ? '+' : ''}
                      {s.rs13} pp/13Wo
                    </span>
                  )}
                  <div className="ml-auto flex shrink-0 gap-1.5">
                    <button
                      type="button"
                      onClick={() => {
                        onAdd(s.symbol, {
                          layer: discoveryTargetLayer(s.gruppe),
                          assetClass: 'etf',
                        });
                        void decide(s, 'uebernommen');
                      }}
                      className="rounded border border-emerald-700/60 bg-emerald-950/40 px-2 py-1 text-[11px] text-emerald-200 hover:brightness-110 focus-ring"
                    >
                      In die Watchlist
                    </button>
                    <button
                      type="button"
                      onClick={() => void decide(s, 'abgelehnt')}
                      className="rounded border border-zinc-700/60 bg-zinc-900/40 px-2 py-1 text-[11px] text-zinc-400 hover:bg-zinc-800/60 focus-ring"
                    >
                      Ablehnen
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </details>
  );
}
